import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Gauge,
  Radio,
  TrendingUp,
  Zap,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getBatteryTests } from '../api/client';
import type { BatteryTestResult } from '../api/client';
import { BatteryHealth } from '../components/console/BatteryHealth';
import { MetricCard } from '../components/console/MetricCard';
import { SlotGridStatus } from '../components/console/SlotGridStatus';
import type { SlotState } from '../components/console/SlotGridStatus';
import { StatusBadge } from '../components/console/StatusBadge';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n';

const defaultHourlyData = [
  { hour: '00:00', tests: 54 },
  { hour: '02:00', tests: 72 },
  { hour: '04:00', tests: 88 },
  { hour: '06:00', tests: 82 },
  { hour: '08:00', tests: 106 },
  { hour: '10:00', tests: 76 },
  { hour: '12:00', tests: 94 },
  { hour: '14:00', tests: 112 },
  { hour: '16:00', tests: 55 },
  { hour: '18:00', tests: 43 },
  { hour: '20:00', tests: 60 },
  { hour: '22:00', tests: 76 },
];

const stationSlots: SlotState[] = [
  'active',
  'idle',
  'good',
  'defect',
  'idle',
  'idle',
  'idle',
  'idle',
  'active',
  'idle',
  'good',
  'good',
  'idle',
  'idle',
  'idle',
  'defect',
];

interface EquipmentUnit {
  name: string;
  status: 'ONLINE' | 'OFFLINE';
  lot: string;
  runtime: string;
  good: number;
  half: number;
  defect: number;
  slots: SlotState[];
}

function classifyHealth(test: BatteryTestResult): number {
  if (!test.configuredMah) return 0;
  return Math.max(0, Math.min(100, (test.measuredMah / test.configuredMah) * 100));
}

function healthColor(value: number): string {
  if (value >= 90) return 'bg-console-good';
  if (value >= 70) return 'bg-console-half';
  return 'bg-console-defect';
}

function formatEquipment(test: BatteryTestResult | undefined, fallback: string): string {
  return test?.equipmentId ?? fallback;
}

export function DashboardPage() {
  const { locale, t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ['battery-tests', 1, 100],
    queryFn: () => getBatteryTests(1, 100),
  });

  const tests = data?.data ?? [];

  const dashboard = useMemo(() => {
    const totalSample = tests.length;
    const good = tests.filter((test) => test.status === 'GOOD').length;
    const half = tests.filter((test) => test.status === 'HALF' || test.status === 'RECARGA').length;
    const defect = tests.filter((test) => test.status === 'DEFECT').length;
    const passRate = totalSample > 0 ? (good / totalSample) * 100 : 98.4;
    const defectRate = totalSample > 0 ? (defect / totalSample) * 100 : 3.1;
    const halfRate = totalSample > 0 ? (half / totalSample) * 100 : 10.5;
    const processed = data?.total ?? 1402;

    const hourlyMap = new Map<string, number>();
    for (const test of tests) {
      if (!test.createdAt) continue;
      const date = new Date(test.createdAt);
      if (Number.isNaN(date.getTime())) continue;
      const hour = `${String(date.getHours()).padStart(2, '0')}:00`;
      hourlyMap.set(hour, (hourlyMap.get(hour) ?? 0) + 1);
    }

    const hourlyData =
      hourlyMap.size > 0
        ? Array.from(hourlyMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .slice(-12)
            .map(([hour, count]) => ({ hour, tests: count }))
        : defaultHourlyData;

    return {
      processed,
      totalSample,
      good,
      half,
      defect,
      passRate,
      defectRate,
      halfRate,
      rechargeRate: totalSample > 0 ? (tests.filter((test) => test.status === 'RECARGA').length / totalSample) * 100 : 2.2,
      hourlyData,
    };
  }, [data?.total, tests]);

  const latestTests = tests.length > 0 ? tests.slice(0, 5) : [];
  const equipmentUnits = useMemo<EquipmentUnit[]>(
    () => [
      {
        name: `${t('nav.equipment')} EQ-DEMO-01`,
        status: 'ONLINE',
        lot: t('app.demoClient'),
        runtime: '04:12:33',
        good: 245,
        half: 18,
        defect: 4,
        slots: ['good', 'good', 'active', 'good', 'good', 'defect', 'good', 'good', 'good', 'good', 'idle', 'idle', 'good', 'good', 'good', 'good'],
      },
      {
        name: `${t('nav.equipment')} EQ-DEMO-02`,
        status: 'ONLINE',
        lot: t('app.demoClient'),
        runtime: '12:44:01',
        good: 1102,
        half: 12,
        defect: 1,
        slots: ['good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good', 'good'],
      },
      {
        name: `${t('nav.equipment')} ${t('dashboard.unassigned')}`,
        status: 'OFFLINE',
        lot: t('dashboard.none'),
        runtime: t('dashboard.lastActiveDemo'),
        good: 0,
        half: 0,
        defect: 0,
        slots: ['offline', 'offline', 'offline', 'offline', 'defect', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline', 'offline'],
      },
    ],
    [t],
  );

  return (
    <div className="mx-auto max-w-[1560px] space-y-6">
      <section className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-4xl font-bold tracking-normal text-console-text">{t('dashboard.title')}</h1>
          <p className="mt-2 text-lg text-console-muted">{t('dashboard.subtitle')}</p>
        </div>
        <div className="rounded border border-console-border bg-console-panel px-4 py-3 text-right">
          <p className="console-label">{t('dashboard.currentLoad')}</p>
          <div className="mt-1 flex items-center justify-end gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-console-good" />
            <span className="font-mono text-sm font-bold uppercase text-console-good">{t('dashboard.systemsNominal')}</span>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title={t('dashboard.totalTests')} value={dashboard.processed.toLocaleString(locale)} trend={t('dashboard.totalTestsTrend')} tone="blue" icon={Gauge} />
        <MetricCard title={t('dashboard.passRate')} value={`${dashboard.passRate.toFixed(1)}%`} trend={t('dashboard.passRateTrend')} tone="good" icon={CheckCircle2} />
        <MetricCard title={t('dashboard.processingCount')} value="42" trend={t('dashboard.processingTrend')} icon={Activity} />
        <MetricCard title={t('dashboard.onlineEquipment')} value="15/16" trend={t('dashboard.onlineEquipmentTrend')} tone="blue" icon={Radio} />
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.6fr_0.8fr]">
        <div className="console-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('dashboard.throughput')}</h2>
            <div className="flex rounded border border-console-border bg-console-panelHigh p-1">
              <button className="rounded bg-console-blueSoft px-3 py-1.5 text-xs font-bold uppercase text-[#00285d]" type="button">
                {t('dashboard.hourly')}
              </button>
              <button className="px-3 py-1.5 text-xs font-bold uppercase text-console-muted hover:text-console-text" type="button">
                {t('dashboard.daily')}
              </button>
            </div>
          </div>
          <div className="h-80">
            {isLoading ? (
              <div className="flex h-full items-center justify-center font-mono text-sm text-console-muted">{t('dashboard.loadingTelemetry')}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboard.hourlyData}>
                  <CartesianGrid stroke="rgba(173, 198, 255, 0.06)" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: '#9aa4bd', fontSize: 12, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip
                    cursor={{ fill: 'rgba(173, 198, 255, 0.06)' }}
                    contentStyle={{
                      background: '#131b2e',
                      border: '1px solid rgba(173, 198, 255, 0.18)',
                      color: '#dae2fd',
                      borderRadius: 4,
                    }}
                  />
                  <Bar dataKey="tests" fill="#3f4b66" stroke="#adc6ff" strokeWidth={1} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="console-card p-5">
          <h2 className="text-xl font-semibold">{t('dashboard.yieldSegmentation')}</h2>
          <div className="mt-8 space-y-7">
            <BatteryHealth label={t('dashboard.goodLabel')} value={Math.max(0, Math.min(100, dashboard.passRate))} status="GOOD" />
            <BatteryHealth label={t('dashboard.halfLabel')} value={Math.max(0, Math.min(100, dashboard.halfRate))} status="HALF" />
            <BatteryHealth label={t('dashboard.defectLabel')} value={Math.max(0, Math.min(100, dashboard.defectRate))} status="DEFECT" />
            <BatteryHealth label={t('dashboard.recargaLabel')} value={Math.max(0, Math.min(100, dashboard.rechargeRate))} status="RECARGA" />
          </div>
          <p className="mt-10 border-t border-console-border pt-5 text-sm text-console-muted">{t('dashboard.traceability')}</p>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-[0.7fr_2.2fr]">
        <div className="space-y-5">
          <div className="console-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{t('dashboard.stationSlots')}</h2>
              <GridIcon />
            </div>
            <SlotGridStatus slots={stationSlots} columns={4} />
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-console-muted">
              <LegendDot className="bg-console-blueSoft" label={t('dashboard.active')} />
              <LegendDot className="bg-console-good" label={t('dashboard.done')} />
              <LegendDot className="bg-console-defect" label={t('dashboard.fault')} />
            </div>
          </div>

          <div className="console-card p-5">
            <h2 className="mb-4 text-xl font-semibold">{t('dashboard.linkedHardware')}</h2>
            <div className="space-y-3">
              <HardwareRow name="EQ-DEMO-01" detail={t('dashboard.activeStation')} status="ONLINE" />
              <HardwareRow name="EQ-DEMO-02" detail={t('dashboard.maintenanceQueue')} status="ONLINE" />
              <HardwareRow name={t('dashboard.unassigned').toUpperCase()} detail={t('status.offline')} status="OFFLINE" />
            </div>
          </div>
        </div>

        <div className="console-card overflow-hidden">
          <div className="flex flex-col justify-between gap-3 border-b border-console-border p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-semibold">{t('dashboard.recentAnalyses')}</h2>
              <p className="text-sm text-console-muted">{t('dashboard.recentAnalysesSubtitle')}</p>
            </div>
            <Link className="console-button" to="/battery-tests">{t('dashboard.viewAllLogs')}</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-console-border bg-console-panelHigh text-xs uppercase tracking-[0.08em] text-console-muted">
                <tr>
                  <th className="px-5 py-4">{t('dashboard.serialNumber')}</th>
                  <th className="px-5 py-4">{t('reports.status')}</th>
                  <th className="px-5 py-4">{t('dashboard.healthSoh')}</th>
                  <th className="px-5 py-4">{t('reports.equipment')}</th>
                  <th className="px-5 py-4">{t('dashboard.timestamp')}</th>
                </tr>
              </thead>
              <tbody>
                {(latestTests.length > 0 ? latestTests : fallbackTests).map((test, index) => {
                  const health = 'health' in test ? test.health : classifyHealth(test);
                  const status = test.status;
                  const serial = 'serialNumber' in test ? test.serialNumber : test.serial;
                  const equipment = 'equipmentId' in test ? formatEquipment(test, t('dashboard.unassigned')) : t(test.equipmentKey);
                  const createdAt = 'createdAt' in test && test.createdAt ? new Date(test.createdAt).toLocaleTimeString(locale) : fallbackTimes[index];

                  return (
                    <tr key={`${serial}-${index}`} className="border-b border-console-border/70 hover:bg-console-panelHigh/60">
                      <td className="px-5 py-4 font-mono font-bold text-console-blueSoft">{serial}</td>
                      <td className="px-5 py-4">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-2 w-36 overflow-hidden rounded-full bg-console-line">
                            <div className={`h-full rounded-full ${healthColor(health)}`} style={{ width: `${health}%` }} />
                          </div>
                          <span className="font-mono text-xs font-bold">{health.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-console-muted">{equipment}</td>
                      <td className="px-5 py-4 font-mono text-console-muted">{createdAt}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">{t('dashboard.liveEquipmentStatus')}</h2>
          <button className="console-button" type="button">{t('dashboard.viewFullAnalytics')}</button>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {equipmentUnits.map((unit) => (
            <EquipmentCard key={unit.name} unit={unit} t={t} />
          ))}
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-[1fr_1fr]">
        <div className="console-card min-h-80 overflow-hidden p-6">
          <div className="flex h-full min-h-72 flex-col justify-end rounded-md border border-console-border bg-gradient-to-b from-slate-300/90 to-console-panelHigh p-6">
            <div className="max-w-md rounded border border-console-blueSoft/70 bg-console-panelHigh/90 p-5 shadow-glass">
              <h3 className="text-lg font-bold text-console-blueSoft">{t('dashboard.livePerformanceHeatmap')}</h3>
              <p className="mt-3 text-sm text-console-muted">{t('dashboard.heatmapDescription')}</p>
              <div className="mt-5 flex gap-8">
                <MetricMini label={t('dashboard.temp')} value="42.4C" tone="text-red-300" />
                <MetricMini label={t('dashboard.volt')} value="3.82V" tone="text-console-blueSoft" />
              </div>
            </div>
          </div>
        </div>

        <div className="console-card p-6">
          <h2 className="text-xl font-semibold">{t('dashboard.anomalyQueue')}</h2>
          <div className="mt-5 space-y-4">
            <Anomaly icon={AlertTriangle} title={t('dashboard.voltageSag')} detail={`EQ-DEMO-01 | ${t('dashboard.slot')}-03 | ${t('app.demoClient').toUpperCase()}`} time="14:02:11" tone="text-console-defect" />
            <Anomaly icon={Zap} title={t('dashboard.capacitanceOutOfRange')} detail={`EQ-DEMO-02 | ${t('dashboard.slot')}-12 | ${t('app.demoClient').toUpperCase()}`} time="13:58:44" tone="text-console-half" />
            <Anomaly icon={TrendingUp} title={t('dashboard.minorThermalDrift')} detail={`${t('dashboard.unassigned').toUpperCase()} | ${t('dashboard.system')} | ${t('app.localDevelopment').toUpperCase()}`} time="13:45:22" tone="text-console-half" />
          </div>
          <button className="console-button mt-8 w-full" type="button">{t('dashboard.runDiagnostics')}</button>
        </div>
      </section>
    </div>
  );
}

const fallbackTimes = ['14:22:05', '14:18:32', '14:12:09', '14:05:44', '13:58:02'];

const fallbackTests = [
  { serial: 'BAT-DEMO-001', status: 'GOOD' as const, health: 98.2, equipmentKey: 'dashboard.unassigned' as const },
  { serial: 'BAT-DEMO-002', status: 'HALF' as const, health: 55.6, equipmentKey: 'dashboard.unassigned' as const },
  { serial: 'BAT-DEMO-003', status: 'DEFECT' as const, health: 16.8, equipmentKey: 'dashboard.unassigned' as const },
];

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-2.5 w-2.5 rounded-full ${className}`} />
      {label}
    </span>
  );
}

function GridIcon() {
  return <Cpu className="text-console-blueSoft" size={20} />;
}

function HardwareRow({ name, detail, status }: { name: string; detail: string; status: 'ONLINE' | 'OFFLINE' }) {
  return (
    <div className="flex items-center justify-between rounded border border-console-border bg-console-base/70 p-3">
      <div>
        <p className="font-mono text-sm text-console-text">{name}</p>
        <p className="console-label mt-1">{detail}</p>
      </div>
      <StatusBadge status={status} />
    </div>
  );
}

function EquipmentCard({ unit, t }: { unit: EquipmentUnit; t: (key: TranslationKey) => string }) {
  const total = unit.good + unit.half + unit.defect;
  const goodWidth = total > 0 ? (unit.good / total) * 100 : 0;
  const halfWidth = total > 0 ? (unit.half / total) * 100 : 0;
  const defectWidth = total > 0 ? (unit.defect / total) * 100 : 0;

  return (
    <article className="console-card p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold">{unit.name}</h3>
          <div className="mt-2">
            <StatusBadge status={unit.status} />
          </div>
        </div>
        <button className="rounded border border-console-blueSoft/40 bg-console-blueSoft/10 px-2 py-1 text-[10px] font-bold uppercase text-console-blueSoft" type="button">
          {t('dashboard.analyse')}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <InfoBox label={t('dashboard.currentLot')} value={unit.lot} />
        <InfoBox label={unit.status === 'OFFLINE' ? t('dashboard.lastActive') : t('dashboard.runtime')} value={unit.runtime} />
      </div>

      <div className="mt-5">
        <p className="console-label mb-2">{t('dashboard.throughputCounters')}</p>
        <div className="flex h-2 overflow-hidden rounded-full bg-console-line">
          <span className="bg-console-good" style={{ width: `${goodWidth}%` }} />
          <span className="bg-console-half" style={{ width: `${halfWidth}%` }} />
          <span className="bg-console-defect" style={{ width: `${defectWidth}%` }} />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
          <span className="text-console-good">{t('status.good')}: {unit.good || '--'}</span>
          <span className="text-console-half">{t('dashboard.proc')}: {unit.half || '--'}</span>
          <span className="text-console-defect">{t('dashboard.fail')}: {unit.defect || '--'}</span>
        </div>
      </div>

      <div className="mt-5">
        <p className="console-label mb-2">{t('dashboard.slotGridStatus')}</p>
        <SlotGridStatus slots={unit.slots} columns={8} compact />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button className="console-button" type="button">{t('dashboard.assignLot')}</button>
        <button className="console-button" type="button">{t('dashboard.modeConfig')}</button>
      </div>
    </article>
  );
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-console-border bg-console-panelHigh p-3">
      <p className="console-label">{label}</p>
      <p className="console-value mt-1 text-sm">{value}</p>
    </div>
  );
}

function MetricMini({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div>
      <p className="console-label">{label}</p>
      <p className={`console-value mt-1 text-lg font-bold ${tone}`}>{value}</p>
    </div>
  );
}

function Anomaly({
  icon: Icon,
  title,
  detail,
  time,
  tone,
}: {
  icon: typeof AlertTriangle;
  title: string;
  detail: string;
  time: string;
  tone: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded border border-console-blueSoft/60 bg-console-panelHigh p-4">
      <Icon className={tone} size={24} />
      <div className="min-w-0 flex-1">
        <p className="font-bold">{title}</p>
        <p className="truncate font-mono text-xs font-bold uppercase text-console-muted">{detail}</p>
      </div>
      <p className="font-mono text-sm text-console-muted">{time}</p>
    </div>
  );
}
